import React from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { directorMessages as messages } from '../messages/directorMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the director steps; each part uses its own slice of it */
import styles from './DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import shell from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

export type DirectorWorkKind = 'stories' | 'styles' | 'script';

interface WorkCopy {
  title: MessageDescriptor;
  text: MessageDescriptor;
  steps: readonly MessageDescriptor[];
  time: MessageDescriptor;
}

const COPY: Readonly<Record<DirectorWorkKind, WorkCopy>> = {
  stories: {
    title: messages.workingStories,
    text: messages.workingText,
    steps: [messages.stepRead, messages.stepCast, messages.stepWriteStories],
    time: messages.aboutMinute,
  },
  styles: {
    title: messages.workingStyles,
    text: messages.workingText,
    steps: [messages.stepRead, messages.stepStory, messages.stepWriteStyles],
    time: messages.aboutMinute,
  },
  script: {
    title: messages.workingScript,
    text: messages.workingScriptText,
    steps: [messages.stepRead, messages.stepStory, messages.stepWriteScript],
    time: messages.aboutTwoMinutes,
  },
};

interface DirectorWorkingProps {
  kind: DirectorWorkKind;
  /** The three grey cards under the box, where the choices come (stories and styles). */
  withSkeletons?: boolean;
}

/**
 * "The director is writing" (design c2): one state for every director step. The steps are the
 * work of a run; the last one is the step in progress. The screen moves on with the realtime event.
 */
const DirectorWorkingComponent: React.FC<DirectorWorkingProps> = ({ kind, withSkeletons = false }) => {
  const intl = useIntl();
  const copy = COPY[kind];
  const last = copy.steps.length - 1;
  return (
    <>
      <div className={styles.work} role="status" data-testid="director-working">
        <div className={styles.ring} aria-hidden />
        <h3>{intl.formatMessage(copy.title)}</h3>
        <p className={styles.workText}>{intl.formatMessage(copy.text)}</p>
        <div className={styles.workSteps}>
          {copy.steps.map((step, index) => (
            <div
              key={step.id}
              className={clsx(styles.workStep, index < last ? styles.workStepDone : styles.workStepNow)}
            >
              <i aria-hidden />
              <span>{intl.formatMessage(step)}</span>
              {index === last && <small>{intl.formatMessage(copy.time)}</small>}
            </div>
          ))}
        </div>
      </div>
      {withSkeletons && (
        <div className={styles.choices} aria-hidden>
          {[0, 1, 2].map((index) => (
            <div key={index} className={styles.skeleton}>
              <div className={shell.shimmer} />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export const DirectorWorking = TypedMemo(DirectorWorkingComponent);
